import { useCallback, useEffect, useRef, useState } from "react";

/** Long enough to read a slide, short enough that a typical sign-in sees both. */
export const LOGIN_PROMO_INTERVAL_MS = 12_000;

/**
 * Remembers which promo the previous visit opened on, so the next visit opens
 * on the other one. A sign-in usually takes less time than one interval, so
 * without this almost nobody would ever see the second slide. Bump the version
 * to reset everyone.
 */
export const LOGIN_PROMO_STORAGE_KEY = "suno_login_promo_last_v1";

function readLastKey(): string | null {
  try {
    return localStorage.getItem(LOGIN_PROMO_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeLastKey(key: string) {
  try {
    localStorage.setItem(LOGIN_PROMO_STORAGE_KEY, key);
  } catch {
    // Storage blocked (private window, quota): every visit opens on the first promo.
  }
}

/** The promo after the one the previous visit opened on; the first one if unknown. */
export function getStartIndex(keys: string[]): number {
  const previous = keys.indexOf(readLastKey() ?? "");
  return previous === -1 ? 0 : (previous + 1) % keys.length;
}

interface Options {
  keys: string[];
  /** Hover, keyboard focus or a hidden tab: hold the current slide. */
  paused: boolean;
  /** False under prefers-reduced-motion: no automatic advance at all. */
  autoRotate: boolean;
  intervalMs?: number;
}

/**
 * Drives the login-screen promo rotation. Pausing keeps the time already
 * spent on a slide, so the progress bar (a CSS animation paused in step) and
 * the timer stay in agreement.
 */
export function useLoginPromoRotation({
  keys,
  paused,
  autoRotate,
  intervalMs = LOGIN_PROMO_INTERVAL_MS
}: Options) {
  const [activeIndex, setActiveIndex] = useState(() => getStartIndex(keys));
  // Bumped on a manual pick so re-picking the current slide still restarts it.
  const [cycle, setCycle] = useState(0);

  // Written from an effect, not the state initializer: StrictMode runs the
  // initializer twice, which would advance the stored key twice and pin every
  // dev visit to the same slide. Writing the same value twice is harmless.
  const startKey = useRef(keys[activeIndex]);
  useEffect(() => {
    if (startKey.current) writeLastKey(startKey.current);
  }, []);

  const remainingRef = useRef(intervalMs);
  const runningFor = useRef<string | null>(null);
  const running = autoRotate && !paused && keys.length > 1;

  useEffect(() => {
    const slideId = `${activeIndex}:${cycle}`;
    if (runningFor.current !== slideId) {
      runningFor.current = slideId;
      remainingRef.current = intervalMs;
    }
    if (!running) return;

    const startedAt = Date.now();
    const timer = window.setTimeout(() => {
      setActiveIndex((index) => (index + 1) % keys.length);
    }, remainingRef.current);
    return () => {
      window.clearTimeout(timer);
      remainingRef.current = Math.max(
        0,
        remainingRef.current - (Date.now() - startedAt)
      );
    };
  }, [activeIndex, cycle, running, intervalMs, keys.length]);

  const select = useCallback((index: number) => {
    setActiveIndex(index);
    setCycle((value) => value + 1);
  }, []);

  return { activeIndex, cycle, running, select };
}
